import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axios";
import { motion, AnimatePresence } from "framer-motion";

import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { FiInfo } from "react-icons/fi";
import {
  FaEdit,
  FaTrash,
  FaGripVertical,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import { useDragReorder } from "../../hooks/useDragReorder";

const SDGCardSkeleton = () => (
  <div className="rounded-xl shadow-sm bg-white border p-4 animate-pulse">
    <div className="flex justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded" />
        <div className="w-12 h-3 bg-gray-200 rounded" />
      </div>
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded" />
        <div className="w-8 h-8 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="h-32 bg-gray-200 rounded-lg" />
    <div className="mt-4 h-5 bg-gray-200 rounded" />
  </div>
);

const EmptyView = ({ onAdd }) => (
  <div className="p-8 border border-dashed border-gray-300 text-center bg-white rounded-lg">
    <h2 className="text-gray-700 font-medium mb-2">No SDGs Found</h2>
    <p className="text-gray-500 text-sm mb-4">
      You haven't added any Sustainable Development Goals yet.
    </p>
    <button
      onClick={onAdd}
      className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90"
    >
      + Add SDG
    </button>
  </div>
);

const SDGPreviewCard = ({ sdg }) => {
  const formattedTitle = (() => {
    if (!sdg.title) return "";

    const words = sdg.title.split(" ");
    if (words.length <= 2) {
      const firstLine = words[0] || "";
      const secondLine = words[1] || "\u200B";
      return `${firstLine}\n${secondLine}`;
    } else {
      const midPoint = Math.ceil(words.length / 2);
      const firstLine = words.slice(0, midPoint).join(" ");
      const secondLine = words.slice(midPoint).join(" ");
      return `${firstLine}\n${secondLine}`;
    }
  })();

  return (
    <div
      style={{ backgroundColor: sdg.bgColor }}
      className="flex flex-col items-center justify-around h-[10rem] pl-4 pr-2 py-3 
      rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-lg rounded-bl-lg text-white cursor-pointer"
    >
      <div className="flex w-full mx-auto items-start">
        <span className="text-3xl font-bold">{sdg.sdgNumber}</span>
        <h3 className="pl-1 mt-1 font-semibold text-sm whitespace-pre-line leading-tight">
          {formattedTitle}
        </h3>
      </div>

      <img
        src={sdg.icon}
        alt={sdg.title}
        draggable={false}
        className="size-16 mt-1 object-contain"
      />
    </div>
  );
};

const AdminSDGs = () => {
  const {
    list: sdgs,
    original,
    orderChanged,
    initialize,
    onDragStart,
    onDragOver,
    onDrop,
    cancelOrder,
    saveOrder,
    setList: setSdgs,
  } = useDragReorder();

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const fetchSDGs = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get("/sdgs/admin");
      const list = resp?.data?.data ?? [];
      const sorted = list.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      initialize(sorted);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load SDGs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSDGs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveToServer = async (updatedList) => {
    await Promise.all(
      updatedList.map((item) =>
        axiosInstance.put(`/sdgs/${item._id}`, {
          sequence: item.sequence,
        })
      )
    );
    toast.success("Order updated");
  };

  const handleSave = () => saveOrder(saveToServer);

  const handleDelete = (sdg) => {
    Swal.fire({
      title: "Delete SDG?",
      text: sdg.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/sdgs/${sdg._id}`);
          setSdgs((p) => p.filter((x) => x._id !== sdg._id));
          toast.success("Deleted");
        } catch (e) {
          console.error(e);
          toast.error("Delete failed");
        }
      }
    });
  };

  const toggleStatus = async (sdg) => {
    try {
      const resp = await axiosInstance.put(`/sdgs/${sdg._id}`, {
        isActive: !sdg.isActive,
      });

      const updated = resp?.data?.data ?? resp?.data;
      setSdgs((p) => p.map((x) => (x._id === sdg._id ? updated : x)));
      toast.success("Status updated");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update status");
    }
  };

  const handleSearch = (q) => {
    if (!q) {
      setSdgs(original);
      return;
    }
    const lower = q.toLowerCase();
    setSdgs(original.filter((x) => x.title.toLowerCase().includes(lower)));
  };

  const handleAddNew = () => {
    navigate("/admin-sdgs-edit");
  };

  const handleEdit = (item) => {
    navigate(`/admin-sdgs-edit/${item._id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSearchBar
        title="Sustainable Development Goals"
        hideAddButton={false}
        onAddNewClick={handleAddNew}
        onSearchClick={handleSearch}
      />
      <div className="my-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded">
        <FiInfo className="text-primaryColor min-w-4 min-h-4" />
        <span>
          Drag cards to adjust their order. Click <strong>Edit</strong> to
          update title, icon, sequence, sdg number, and background color.
        </span>
      </div>
      {/* SAVE ORDER BAR */}
      {orderChanged && (
        <div className="px-6 py-3 bg-yellow-100 border-b border-yellow-300 flex items-center justify-between">
          <div className="flex items-center gap-2 text-yellow-800">
            <FiInfo />
            <span>Order changed. Save changes?</span>
          </div>
          <div className="flex gap-3">
            <button
              className="px-4 py-1 bg-primaryColor text-white rounded"
              onClick={handleSave}
            >
              Save Order
            </button>
            <button className="px-4 py-1 border rounded" onClick={cancelOrder}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4  gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SDGCardSkeleton key={i} />
            ))}
          </div>
        ) : sdgs.length === 0 ? (
          <EmptyView onAdd={handleAddNew} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4  gap-4">
            <AnimatePresence>
              {sdgs.map((sdg, index) => (
                <motion.div
                  key={sdg._id}
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  draggable
                  onDragStart={(e) => onDragStart(e, index)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, index)}
                  className="bg-white border rounded-xl shadow-sm p-3"
                >
                  {/* Top Controls */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                      <FaGripVertical />
                      <span>Seq {sdg.sequence}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(sdg)}
                        className="p-2 rounded hover:bg-gray-100"
                      >
                        <FaEdit className="text-primaryColor" />
                      </button>

                      <button
                        onClick={() => handleDelete(sdg)}
                        className="p-2 rounded hover:bg-red-50"
                      >
                        <FaTrash className="text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* SDG CARD */}
                  <SDGPreviewCard sdg={sdg} />

                  {/* STATUS */}
                  <div className="mt-3 flex justify-center">
                    <button
                      onClick={() => toggleStatus(sdg)}
                      className={`flex items-center gap-2 text-xs px-3 py-1 rounded ${
                        sdg.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {sdg.isActive ? (
                        <FaToggleOn className="text-green-600" />
                      ) : (
                        <FaToggleOff className="text-gray-600" />
                      )}
                      {sdg.isActive ? "Active" : "Inactive"}
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSDGs;

// import React, { useEffect, useRef, useState } from "react";
// import axiosInstance from "../../utils/axios";
// import { motion, AnimatePresence } from "framer-motion";

// import { useNavigate } from "react-router-dom";
// import toast from "react-hot-toast";
// import Swal from "sweetalert2";
// import { FiInfo } from "react-icons/fi";
// import {
//   FaEdit,
//   FaTrash,
//   FaGripVertical,
//   FaToggleOn,
//   FaToggleOff,
// } from "react-icons/fa";
// import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";

// const SDGCardSkeleton = () => (
//   <div className="rounded-xl shadow-sm bg-white border p-4 animate-pulse">
//     <div className="flex justify-between mb-3">
//       <div className="flex items-center gap-2">
//         <div className="w-4 h-4 bg-gray-200 rounded" />
//         <div className="w-12 h-3 bg-gray-200 rounded" />
//       </div>
//       <div className="flex gap-2">
//         <div className="w-8 h-8 bg-gray-200 rounded" />
//         <div className="w-8 h-8 bg-gray-200 rounded" />
//       </div>
//     </div>
//     <div className="h-32 bg-gray-200 rounded-lg" />
//     <div className="mt-4 h-5 bg-gray-200 rounded" />
//   </div>
// );

// const EmptyView = ({ onAdd }) => (
//   <div className="p-8 border border-dashed border-gray-300 text-center bg-white rounded-lg">
//     <h2 className="text-gray-700 font-medium mb-2">No SDGs Found</h2>
//     <p className="text-gray-500 text-sm mb-4">
//       You haven't added any Sustainable Development Goals yet.
//     </p>
//     <button
//       onClick={onAdd}
//       className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90"
//     >
//       + Add SDG
//     </button>
//   </div>
// );

// const SDGPreviewCard = ({ sdg }) => {
//   const formattedTitle = (() => {
//     if (!sdg.title) return "";

//     const words = sdg.title.split(" ");
//     if (words.length <= 2) {
//       const firstLine = words[0] || "";
//       const secondLine = words[1] || "\u200B";
//       return `${firstLine}\n${secondLine}`;
//     } else {
//       const midPoint = Math.ceil(words.length / 2);
//       const firstLine = words.slice(0, midPoint).join(" ");
//       const secondLine = words.slice(midPoint).join(" ");
//       return `${firstLine}\n${secondLine}`;
//     }
//   })();

//   return (
//     <div
//       style={{ backgroundColor: sdg.bgColor }}
//       className="flex flex-col items-center justify-around h-[10rem] pl-4 pr-2 py-3
//       rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-lg rounded-bl-lg text-white cursor-pointer"
//     >
//       <div className="flex w-full mx-auto items-start">
//         <span className="text-3xl font-bold">{sdg.sdgNumber}</span>
//         <h3 className="pl-1 mt-1 font-semibold text-sm whitespace-pre-line leading-tight">
//           {formattedTitle}
//         </h3>
//       </div>

//       <img
//         src={sdg.icon}
//         alt={sdg.title}
//         draggable={false}
//         className="size-16 mt-1 object-contain"
//       />
//     </div>
//   );
// };

// const AdminSDGs = () => {
//   const [sdgs, setSdgs] = useState([]);
//   const [original, setOriginal] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [orderChanged, setOrderChanged] = useState(false);

//   const dragIndexRef = useRef(null);
//   const navigate = useNavigate();

//   const fetchSDGs = async () => {
//     try {
//       setLoading(true);
//       const resp = await axiosInstance.get("/sdgs/admin");
//       const list = resp?.data?.data ?? [];
//       const sorted = list.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
//       setSdgs(sorted);
//       setOriginal(JSON.parse(JSON.stringify(sorted)));
//     } catch (e) {
//       console.error(e);
//       toast.error("Failed to load SDGs");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchSDGs();
//   }, []);

//   const onDragStart = (e, index) => {
//     dragIndexRef.current = index;
//     e.dataTransfer.effectAllowed = "move";
//     try {
//       e.dataTransfer.setData("text/plain", index.toString());
//     } catch {}
//   };

//   const onDragOver = (e) => e.preventDefault();

//   const onDrop = (e, dropIndex) => {
//     e.preventDefault();
//     const fromIndex =
//       dragIndexRef.current ?? Number(e.dataTransfer.getData("text/plain"));

//     if (fromIndex === dropIndex) return;

//     const cloned = [...sdgs];
//     const [moved] = cloned.splice(fromIndex, 1);
//     cloned.splice(dropIndex, 0, moved);

//     const updated = cloned.map((item, i) => ({
//       ...item,
//       sequence: i + 1,
//     }));

//     setSdgs(updated);

//     const changed = updated.some((u) => {
//       const orig = original.find((o) => o._id === u._id);
//       return orig && orig.sequence !== u.sequence;
//     });

//     setOrderChanged(changed);
//   };

//   const saveOrder = async () => {
//     try {
//       await Promise.all(
//         sdgs.map((item) =>
//           axiosInstance.put(`/sdgs/${item._id}`, {
//             sequence: item.sequence,
//           })
//         )
//       );

//       toast.success("Order updated");
//       setOriginal(JSON.parse(JSON.stringify(sdgs)));
//       setOrderChanged(false);
//     } catch (e) {
//       console.error(e);
//       toast.error("Failed to save order");
//     }
//   };

//   const cancelOrder = () => {
//     console.log("original:", original);
//     setSdgs(JSON.parse(JSON.stringify(original)));
//     setOrderChanged(false);
//   };

//   const handleDelete = (sdg) => {
//     Swal.fire({
//       title: "Delete SDG?",
//       text: sdg.title,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonText: "Yes, delete",
//     }).then(async (result) => {
//       if (result.isConfirmed) {
//         try {
//           await axiosInstance.delete(`/sdgs/${sdg._id}`);
//           setSdgs((p) => p.filter((x) => x._id !== sdg._id));
//           toast.success("Deleted");
//         } catch (e) {
//           console.error(e);
//           toast.error("Delete failed");
//         }
//       }
//     });
//   };

//   const toggleStatus = async (sdg) => {
//     try {
//       const resp = await axiosInstance.put(`/sdgs/${sdg._id}`, {
//         isActive: !sdg.isActive,
//       });

//       const updated = resp?.data?.data ?? resp?.data;
//       setSdgs((p) => p.map((x) => (x._id === sdg._id ? updated : x)));
//       toast.success("Status updated");
//     } catch (e) {
//       console.error(e);
//       toast.error("Failed to update status");
//     }
//   };

//   const handleSearch = (q) => {
//     if (!q) {
//       setSdgs(original);
//       return;
//     }
//     const lower = q.toLowerCase();
//     setSdgs(original.filter((x) => x.title.toLowerCase().includes(lower)));
//   };

//   const handleAddNew = () => {
//     navigate("/admin-sdgs-edit");
//   };

//   const handleEdit = (item) => {
//     navigate(`/admin-sdgs-edit/${item._id}`);
//   };

//   return (
//     <div className="min-h-screen bg-gray-50">
//       <AdminSearchBar
//         title="Sustainable Development Goals"
//         hideAddButton={false}
//         onAddNewClick={handleAddNew}
//         onSearchClick={handleSearch}
//       />

//       {/* SAVE ORDER BAR */}
//       {orderChanged && (
//         <div className="px-6 py-3 bg-yellow-100 border-b border-yellow-300 flex items-center justify-between">
//           <div className="flex items-center gap-2 text-yellow-800">
//             <FiInfo />
//             <span>Order changed. Save changes?</span>
//           </div>
//           <div className="flex gap-3">
//             <button
//               className="px-4 py-1 bg-primaryColor text-white rounded"
//               onClick={saveOrder}
//             >
//               Save Order
//             </button>
//             <button className="px-4 py-1 border rounded" onClick={cancelOrder}>
//               Cancel
//             </button>
//           </div>
//         </div>
//       )}

//       {/* CONTENT */}
//       <div className="px-6 py-6">
//         {loading ? (
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4  gap-4">
//             {Array.from({ length: 8 }).map((_, i) => (
//               <SDGCardSkeleton key={i} />
//             ))}
//           </div>
//         ) : sdgs.length === 0 ? (
//           <EmptyView onAdd={handleAddNew} />
//         ) : (
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4  gap-4">
//             <AnimatePresence>
//               {sdgs.map((sdg, index) => (
//                 <motion.div
//                   key={sdg._id}
//                   layout
//                   transition={{ type: "spring", stiffness: 300, damping: 25 }}
//                   draggable
//                   onDragStart={(e) => onDragStart(e, index)}
//                   onDragOver={onDragOver}
//                   onDrop={(e) => onDrop(e, index)}
//                   className="bg-white border rounded-xl shadow-sm p-3"
//                 >
//                   {/* Top Controls */}
//                   <div className="flex justify-between items-center mb-2">
//                     <div className="flex items-center gap-2 text-gray-500 text-xs">
//                       <FaGripVertical />
//                       <span>Seq {sdg.sequence}</span>
//                     </div>

//                     <div className="flex items-center gap-2">
//                       <button
//                         onClick={() => handleEdit(sdg)}
//                         className="p-2 rounded hover:bg-gray-100"
//                       >
//                         <FaEdit className="text-primaryColor" />
//                       </button>

//                       <button
//                         onClick={() => handleDelete(sdg)}
//                         className="p-2 rounded hover:bg-red-50"
//                       >
//                         <FaTrash className="text-red-600" />
//                       </button>
//                     </div>
//                   </div>

//                   {/* SDG CARD */}
//                   <SDGPreviewCard sdg={sdg} />

//                   {/* STATUS */}
//                   <div className="mt-3 flex justify-center">
//                     <button
//                       onClick={() => toggleStatus(sdg)}
//                       className={`flex items-center gap-2 text-xs px-3 py-1 rounded ${
//                         sdg.isActive
//                           ? "bg-green-100 text-green-700"
//                           : "bg-gray-200 text-gray-700"
//                       }`}
//                     >
//                       {sdg.isActive ? (
//                         <FaToggleOn className="text-green-600" />
//                       ) : (
//                         <FaToggleOff className="text-gray-600" />
//                       )}
//                       {sdg.isActive ? "Active" : "Inactive"}
//                     </button>
//                   </div>
//                 </motion.div>
//               ))}
//             </AnimatePresence>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default AdminSDGs;
